import { useCallback, useEffect, useMemo } from 'react';
import { IncomingControlMessageType, } from '@crew/enums/domain';
import { useSubscriptionService } from '@crew/states';
import { useWebsocketContext } from './websocketProvider';
/**
 * 指定したリソースと非同期通信するためのcustom hook
 * @param param0
 * @param enable 接続するか否か。falseにすると接続しない。trueか省略すると接続開始する
 * @returns
 */
export const useWebsocket = ({ targetType, targetId, dataType, messageTypeFilter, IsValidMessage, onRecvMessage, }, enable = true) => {
    // サブスクリプションID。イベントハンドラの管理用に使用する。
    //   ・ユーザーのmeta、dataチャンネル　　　　: {targetType}_{dataType}_{messageType}
    //                                  　　　　 例）user_meta_feed_unread_added、user_data_feed_item_added
    //   ・チャットルームのmeta、dataチャンネル　: {targetType}_{targetId}_{dataType}_{messageType}
    //                                        　 例）chat_room_{chatRoomId}_meta_unread_added、chat_room_{chatRoomId}_data_chat_message_added
    //   ・チャットスレッドのmeta、dataチャンネル：{targetType}_{targetId}_{dataType}_{messageType}
    //                                          例）chat_thread_{chatThreadId}_meta_unread_added、chat_thread_{chatThreadId1}_data_chat_message_added
    const subscriptionId = useMemo(() => {
        return Symbol(`${targetType}${targetId ? `_${targetId}` : ''}_${dataType}_${messageTypeFilter}`);
    }, [dataType, messageTypeFilter, targetId, targetType]);
    const { subscribe, unsubscribe, isSubscribing } = useWebsocketContext();
    const subscriptionService = useSubscriptionService();
    /**
     * messageTypeFilterを基に受信対象のメッセージ種類かチェックする関数
     */
    const isReceiveTargetMessageType = useCallback((type) => messageTypeFilter === type, [messageTypeFilter]);
    /**
     * 非同期通信でメッセージを受信した時に呼び出すイベントハンドラ
     */
    const handleRecvMessage = useCallback((type, message) => {
        if (!isReceiveTargetMessageType(type)) {
            // messageTypeでfilterされた、receiveしない
            console.info(`[useWebsocket] Incoming message is filtered by message type. type:${type}`);
            return;
        }
        if (IsValidMessage !== true && !IsValidMessage(message)) {
            console.warn(`[useWebsocket] Incoming message is invalid. ${JSON.stringify(message)}`);
            return;
        }
        console.info(`[useWebsocket] Incoming message is valid.`);
        onRecvMessage(type, message);
    }, [IsValidMessage, isReceiveTargetMessageType, onRecvMessage]);
    // 制御系メッセージを受信した時に呼び出すイベントハンドラ
    const handleRecvControlMessage = useCallback((type) => {
        console.info(`[useWebsocket] Incoming control message. controlMessageType:${type}`);
        // サブスクライブ成功時、subscribeステータスを更新する
        if (type === IncomingControlMessageType.Success) {
            subscriptionService.updateSubscribeStateSubscribed({
                targetType,
                targetId,
                dataType,
            });
        }
        else {
            console.error(`[useWebsocket] Incoming control message is not success. targetType:${targetType}, targetId:${targetId}, dataType:${dataType}, controlMessageType:${type}`);
        }
    }, [dataType, subscriptionService, targetId, targetType]);
    // サブスクライブ処理
    useEffect(() => {
        if (!enable) {
            return;
        }
        subscribe(subscriptionId, targetType, targetId, dataType, messageTypeFilter, handleRecvMessage, handleRecvControlMessage, subscriptionService.addSubscriptionHandler, subscriptionService.updateSubscribeStateSubscribing);
        return () => {
            unsubscribe(messageTypeFilter, subscriptionId, subscriptionService.deleteSubscriptionHandler, subscriptionService.updateSubscribeStateNotSubscribed);
        };
    }, [
        dataType,
        enable,
        handleRecvControlMessage,
        handleRecvMessage,
        messageTypeFilter,
        subscribe,
        subscriptionId,
        subscriptionService.addSubscriptionHandler,
        subscriptionService.deleteSubscriptionHandler,
        subscriptionService.updateSubscribeStateNotSubscribed,
        subscriptionService.updateSubscribeStateSubscribing,
        targetId,
        targetType,
        unsubscribe,
    ]);
    return isSubscribing(messageTypeFilter);
};
/**
 * Websocketを強制的に切断する関数を取得するcustom hook
 * @returns
 */
export const useWebsocketForceDisconnect = () => {
    const { forceDisconnect } = useWebsocketContext();
    return forceDisconnect;
};
/**
 * Websocketの接続を一時停止・再開する関数を取得するcustom hook
 * @returns
 */
export const useWebsocketPauser = () => {
    const { pause: pauseConnection, resume: resumeConnection } = useWebsocketContext();
    return {
        /**
         * Websocketの接続を一時停止する。
         */
        pauseConnection,
        /**
         * Websocketの接続を再開する。既に接続済の場合は何もしない。
         */
        resumeConnection,
    };
};
